import React from 'react';
import type { LayoutConfig, LayoutContainer as LayoutContainerType, LayoutAlignment } from '../schema';
import { cn } from '../lib/utils';
import { toCssSize } from './responsive';

// ============================================
// Container Props
// ============================================

export interface LayoutContainerProps {
    /** Container ID */
    id?: string;
    /** Layout configuration */
    layout: LayoutConfig;
    /** Children to render */
    children: React.ReactNode;
    /** Additional CSS class */
    className?: string;
    /** Additional inline styles */
    style?: React.CSSProperties;
    /** Container data attributes */
    dataAttributes?: Record<string, string>;
}

// ============================================
// Layout Container Component
// ============================================

/**
 * Container component that applies layout configuration to its children
 */
export function LayoutContainer({
    id,
    layout,
    children,
    className,
    style,
    dataAttributes,
}: LayoutContainerProps): React.ReactElement {
    const containerStyles = buildContainerStyles(layout, style);
    const containerClasses = buildContainerClasses(layout, className);

    return (
        <div
            id={id ? `layout-container-${id}` : undefined}
            data-layout-type={layout.type}
            className={containerClasses}
            style={containerStyles}
            dir={layout.direction}
            {...dataAttributes}
        >
            {children}
        </div>
    );
}

// ============================================
// Style Builders
// ============================================

/**
 * Build container styles based on layout config
 */
function buildContainerStyles(
    layout: LayoutConfig,
    baseStyle?: React.CSSProperties
): React.CSSProperties {
    const styles: React.CSSProperties = { ...baseStyle };

    // Gap
    if (layout.gap !== undefined) {
        styles.gap = toCssSize(layout.gap);
    }

    // Padding
    if (layout.padding !== undefined) {
        styles.padding = toCssSize(layout.padding);
    }

    // Layout type specific styles
    switch (layout.type) {
        case 'single':
            styles.display = 'block';
            break;

        case 'horizontal':
            styles.display = 'flex';
            styles.flexDirection = 'row';
            styles.flexWrap = 'wrap';
            applyFlexAlignment(styles, layout.alignment);
            break;

        case 'vertical':
            styles.display = 'flex';
            styles.flexDirection = 'column';
            applyFlexAlignment(styles, layout.alignment);
            break;

        case 'auto':
            styles.display = 'flex';
            styles.flexDirection = 'column';
            applyFlexAlignment(styles, layout.alignment);
            break;

        case 'grid':
            styles.display = 'grid';
            if (layout.columns) {
                styles.gridTemplateColumns = `repeat(${layout.columns}, minmax(0, 1fr))`;
            }
            applyGridAlignment(styles, layout.alignment);
            break;

        case 'masonry':
            // CSS masonry is still experimental, use columns fallback
            styles.display = 'block';
            if (layout.columns) {
                styles.columnCount = layout.columns;
                styles.columnGap = toCssSize(layout.gap) || '1rem';
            }
            break;

        case 'carousel':
            styles.display = 'flex';
            styles.flexDirection = 'row';
            styles.overflowX = 'auto';
            styles.scrollSnapType = 'x mandatory';
            styles.scrollBehavior = 'smooth';
            break;

        case 'tabs':
            styles.display = 'block';
            break;
    }

    return styles;
}

/**
 * Build container CSS classes based on layout config
 */
function buildContainerClasses(layout: LayoutConfig, baseClass?: string): string {
    return cn(
        'layout-container',
        `layout-container--${layout.type}`,
        layout.responsive && 'layout-container--responsive',
        baseClass
    );
}

/**
 * Apply flex alignment styles
 */
function applyFlexAlignment(
    styles: React.CSSProperties,
    alignment?: LayoutAlignment
): void {
    if (!alignment) return;

    switch (alignment) {
        case 'start':
            styles.justifyContent = 'flex-start';
            styles.alignItems = 'flex-start';
            break;
        case 'center':
            styles.justifyContent = 'center';
            styles.alignItems = 'center';
            break;
        case 'end':
            styles.justifyContent = 'flex-end';
            styles.alignItems = 'flex-end';
            break;
        case 'stretch':
            styles.justifyContent = 'stretch';
            styles.alignItems = 'stretch';
            break;
        case 'space-between':
            styles.justifyContent = 'space-between';
            break;
        case 'space-around':
            styles.justifyContent = 'space-around';
            break;
    }
}

/**
 * Apply grid alignment styles
 */
function applyGridAlignment(
    styles: React.CSSProperties,
    alignment?: LayoutAlignment
): void {
    if (!alignment) return;

    switch (alignment) {
        case 'start':
            styles.justifyItems = 'start';
            styles.alignItems = 'start';
            break;
        case 'center':
            styles.justifyItems = 'center';
            styles.alignItems = 'center';
            break;
        case 'end':
            styles.justifyItems = 'end';
            styles.alignItems = 'end';
            break;
        case 'stretch':
            styles.justifyItems = 'stretch';
            styles.alignItems = 'stretch';
            break;
    }
}

// ============================================
// Default Layouts
// ============================================

/**
 * Default single column layout
 */
export const DEFAULT_SINGLE_LAYOUT: LayoutConfig = {
    type: 'single',
};

/**
 * Default vertical stack layout
 */
export const DEFAULT_VERTICAL_LAYOUT: LayoutConfig = {
    type: 'vertical',
    gap: '1rem',
    alignment: 'stretch',
};

/**
 * Default horizontal layout
 */
export const DEFAULT_HORIZONTAL_LAYOUT: LayoutConfig = {
    type: 'horizontal',
    gap: '1rem',
    alignment: 'start',
};

/**
 * Default grid layout
 */
export const DEFAULT_GRID_LAYOUT: LayoutConfig = {
    type: 'grid',
    columns: 2,
    gap: '1rem',
};

// ============================================
// Layout Container Data Utilities
// ============================================

/**
 * Create a layout container configuration
 */
export function createLayoutContainer(
    id: string,
    layout: LayoutConfig,
    children: (string | LayoutContainerType)[]
): LayoutContainerType {
    return {
        id,
        layout,
        children,
    };
}

/**
 * Get flat list of all item IDs in a container tree
 */
export function getContainerItemIds(container: LayoutContainerType): string[] {
    const ids: string[] = [];

    for (const child of container.children) {
        if (typeof child === 'string') {
            ids.push(child);
        } else {
            ids.push(...getContainerItemIds(child));
        }
    }

    return ids;
}
